import React, { useEffect, useMemo } from "react";

import { FormattedMessage, useIntl } from "react-intl";

import BlackfireIcon from "Components/icons/BlackfireIcon";
import config from "console_config";
import Button, { Link } from "ds/Button";
import useDecodedParams from "Hooks/useDecodedParams";
import { useLocalForage } from "Hooks/useLocalForage";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { meSelector } from "Reducers/app/selectors";
import { environmentsAsArraySelector } from "Reducers/environment";
import {
  blackfireEnabledInEnvironmentSelector,
  blackfireUUIDSelector,
  deploymentHasLanguageSelector,
  isLoadingSelector,
  loadDeploymentCurrent
} from "Reducers/environment/deployment";
import { selectFeatureBlackfireCardEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { getIntegrations, integrationsSelector } from "Reducers/integration";
import { projectCapabilitySelector } from "Reducers/project/capabilities";
import { gitProjectSelector } from "Reducers/project/project";
import { subscriptionSelector } from "Reducers/subscription";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import BlackfireCardSkeleton from "./BlackfireCard.skeleton";
import * as S from "./BlackfireCard.styles";
import useBlackFireSellableStatus, {
  BlackFireSellableStatusType
} from "./useBlackFireSellableStatus";
import { getBlackfireConnectLink } from "./utils";

type BlackFireIntegrationtType = {
  environments_credentials?: Record<string, { [key: string]: string }>;
};

const messages = {
  launch: "blackfire.card.cta.launch",
  learnmore: "blackfire.card.cta.learn_more",
  subscribed: "blackfire.card.message.subscribed",
  unsubscribedPro: "blackfire.card.message.grid.unsubscribed",
  unsubscribedEnterprise: "blackfire.card.message.dg2",
  managePlan: "plan.manage",
  observabilitySuiteTitle: "integration.observability_suite",
  blackfireTitle: "blackfire.title"
};

type PropType = {
  subscriptionId: string;
  mainEnvironmentId: string;
  sellableStatus: BlackFireSellableStatusType;
};

const BlackfireCard = (props: PropType) => {
  const { subscriptionId, mainEnvironmentId, sellableStatus } = props;

  const { environmentId, organizationId, projectId } = useDecodedParams<{
    environmentId: string;
    organizationId: string;
    projectId: string;
  }>();

  const { value: isHidden, setValue: setIsHidden } = useLocalForage(
    `isBlackfireCardHidden-${organizationId}-${projectId}`
  );

  const capabilites = useAppSelector(state =>
    projectCapabilitySelector(state, { projectId, organizationId })
  );

  const {
    isEnterprise,
    canPurchaseFromConsole,
    blackfireOrObservabilityIsAvailable,
    isPurchased
  } = sellableStatus;

  const integration = useSelectorWithUrlParams(integrationsSelector);

  const blackFireIntegration = integration?.find(i => i.type === "blackfire");

  const hasBlackFireIntegration =
    blackFireIntegration || capabilites?.blackfire?.enabled;

  const environmentCredentials = (
    blackFireIntegration as BlackFireIntegrationtType
  )?.environments_credentials?.[environmentId || mainEnvironmentId];

  const formatMessage = useIntl().formatMessage;

  const isEnabledInEnvironment = useAppSelector(state =>
    blackfireEnabledInEnvironmentSelector(state, {
      organizationId,
      projectId,
      environmentId
    })
  );

  const uuid = useAppSelector(state =>
    blackfireUUIDSelector(state, {
      organizationId,
      projectId,
      environmentId
    })
  );

  const serverUUID = uuid || environmentCredentials?.server_uuid;

  const cta = useMemo(() => {
    if (isEnabledInEnvironment || hasBlackFireIntegration) {
      return {
        link: getBlackfireConnectLink(formatMessage, serverUUID),
        buttonLabel: messages.launch,
        message: messages.subscribed
      };
    }

    if (blackfireOrObservabilityIsAvailable) {
      if (isPurchased && hasBlackFireIntegration) {
        return {
          link: getBlackfireConnectLink(formatMessage, serverUUID),
          buttonLabel: messages.launch,
          message: messages.subscribed
        };
      }

      if (canPurchaseFromConsole) {
        return {
          link: `/${encodeURIComponent(
            organizationId
          )}/-/billing/plan/${subscriptionId}`,
          buttonLabel: messages.managePlan,
          message: messages.unsubscribedEnterprise,
          internalLink: true
        };
      } else {
        return {
          link: formatMessage({ id: "links.observability-suite-features" }),
          buttonLabel: messages.learnmore,
          message: messages.unsubscribedPro
        };
      }
    }
    return {
      link: formatMessage({ id: "links.blackfire.features" }),
      buttonLabel: messages.learnmore,
      message: messages.unsubscribedPro
    };
  }, [
    isEnabledInEnvironment,
    hasBlackFireIntegration,
    blackfireOrObservabilityIsAvailable,
    formatMessage,
    serverUUID,
    isPurchased,
    canPurchaseFromConsole,
    organizationId,
    subscriptionId
  ]);

  const user = useAppSelector(meSelector);

  if (isHidden === undefined || isHidden === true) {
    return null;
  }

  return (
    <S.Layout>
      {(!isEnabledInEnvironment || !uuid) && (
        <S.StyledCloseButton onClick={() => setIsHidden(true)} />
      )}
      <S.Heading>
        <FormattedMessage
          id={
            isEnterprise
              ? messages.observabilitySuiteTitle
              : messages.blackfireTitle
          }
        />
      </S.Heading>
      <S.Text>
        <FormattedMessage
          id={cta.message}
          values={{
            br: <br />
          }}
        />
      </S.Text>
      <S.ButtonRow>
        <Button
          analyticId={cta?.buttonLabel}
          href={cta?.link}
          target={cta.internalLink ? undefined : "_blank"}
        >
          <FormattedMessage id={cta?.buttonLabel} />
        </Button>
        <BlackfireIcon />
      </S.ButtonRow>
      {!serverUUID && user?.username && (
        <S.StyledDescription size="small" $color="granite">
          <FormattedMessage
            id="metrics.report.support.text"
            defaultMessage="Having trouble? "
          />
          <Link
            analyticId="metrics.report.support.title"
            href={
              config.CUSTOM_SUPPORT_URL ||
              `/-/users/${user.username}/tickets/open`
            }
          >
            <FormattedMessage id="metrics.report.support.title" />
          </Link>
        </S.StyledDescription>
      )}
    </S.Layout>
  );
};

const BlackFireCardHOC = props => {
  const { environmentId, organizationId, projectId } = useDecodedParams<{
    environmentId?: string;
    organizationId: string;
    projectId: string;
  }>();

  const environments = useSelectorWithUrlParams(environmentsAsArraySelector);
  const isLoading = useAppSelector(isLoadingSelector);

  const hasSupportLanguage = useAppSelector(state =>
    deploymentHasLanguageSelector(state, {
      environmentId,
      organizationId,
      projectId,
      languages: config.CUSTOM_BLACKFIRE_CARD_LANGUAGE
    })
  );

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getIntegrations({ projectId }));
  }, [dispatch, projectId]);

  const mainEnvironmentId = useMemo(
    () => environments?.find(env => env.is_main)?.id,
    [environments]
  );

  useEffect(() => {
    if (environmentId) {
      dispatch(
        loadDeploymentCurrent({
          organizationId,
          projectId,
          environmentId,
          settings: {
            escapeLoading: true
          }
        })
      );
    }
  }, [dispatch, environmentId, organizationId, projectId]);

  const project = useAppSelector(state =>
    gitProjectSelector(state, {
      organizationId,
      projectId
    })
  );

  const subscription = useAppSelector(state =>
    subscriptionSelector(state, {
      organizationId,
      id: project?.getSubscriptionId()
    })
  );

  const sellableStatus = useBlackFireSellableStatus(subscription);

  const isBlackfireEnabled = useAppSelector(selectFeatureBlackfireCardEnabled);

  if (!hasSupportLanguage || !isBlackfireEnabled) return null;

  return isLoading ? (
    <BlackfireCardSkeleton />
  ) : (
    <BlackfireCard
      subscriptionId={subscription?.id}
      sellableStatus={sellableStatus}
      environments={environments}
      mainEnvironmentId={mainEnvironmentId}
      {...props}
    />
  );
};

export default React.memo(BlackFireCardHOC);
